import { ShieldAlert, Trash2 } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useSetSolutionDeletionEnabled,
  useSolutionDeletionEnabled,
} from "@/hooks/use-system-config";

/**
 * System Settings control for the firm-wide "Solution deletion enabled" gate.
 * When off (the default), the RM-only Solution hard-delete affordance is hidden
 * across the app and the delete API refuses. Toggling requires manageConfiguration
 * (RM/CEO) — enforced server-side; the change is audit-logged.
 */
export function SolutionDeletionSettingCard() {
  const { data, isLoading, isError, error, refetch } = useSolutionDeletionEnabled();
  const setEnabled = useSetSolutionDeletionEnabled();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the Solution deletion setting"}
        onRetry={() => refetch()}
      />
    );
  }

  const enabled = data?.enabled === true;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Solution deletion</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Controls whether the permanent, cascading{" "}
          <span className="font-medium text-foreground">Delete Solution</span> action is available
          anywhere in the app. When disabled, the delete buttons are hidden and the delete API is
          refused for everyone. When enabled, the action remains restricted to Resource Managers.
          Deletion is off by default and must be turned on here.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-28 w-full max-w-md" />
      ) : (
        <Card className="border-border/50 max-w-md">
          <CardContent className="space-y-4 p-4">
            <div className="flex items-start gap-3">
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  enabled ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"
                }`}
              >
                {enabled ? <Trash2 className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  Solution deletion is {enabled ? "enabled" : "disabled"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {enabled
                    ? "Resource Managers can permanently delete Solutions."
                    : "No one can delete Solutions until this is turned on."}
                </p>
              </div>
            </div>
            <Button
              variant={enabled ? "outline" : "default"}
              onClick={() => setEnabled.mutate(!enabled)}
              disabled={setEnabled.isPending}
            >
              {setEnabled.isPending
                ? "Saving…"
                : enabled
                  ? "Disable Solution deletion"
                  : "Enable Solution deletion"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
